import { useRouter } from 'next/navigation'
import { useRef, useState, useEffect, useCallback } from 'react'
import { isMobile } from 'react-device-detect'

import { initList } from '@/components/purchase/components/parkinglot/model'

import paymentEvents from '@/utils/gaEvents/paymentEvents'

import type { SharedParkinglotDetail } from '@/api/server/detail/dto/detailDto'
import Shared from '@/shared'
import type { LegacyRef, Dispatch, SetStateAction } from 'react'

import { PARKINGLOT_TYPE } from '@/enums/detail'
import { SHARE_EXTEND } from '@/enums/gtagEvents'
import { PLATFORM_TYPE } from '@/enums/purchase'

import { requestSharedParkinglotPayment } from '@/api/client/purchase/index'
import { ClientRequestError } from '@/lib/axiosClient'

export type PaymentMethodList = {
  value: string
  name: string
  id: string
  checked: boolean
}[]

export type PostMessage = {
  result: string
  parkingSeq: number | null
  guestCode: string
  errorMsg: string | null
  couSeq: number | null
}

export interface SharedParkinglotPurchaseViewModel {
  texts: object
  states: {
    step: number
    isErrorToast: boolean
    isComplete: boolean
    isOpen: boolean
    isInComplete: boolean
    frameUrl: string
    toastText: string
    ticketSeq: number
    windowHeight: number
    contentHeight: number
    headerRef: LegacyRef<HTMLDivElement> | null
    footerRef: LegacyRef<HTMLDivElement> | null
    paymentMethodList: PaymentMethodList
    isPurchase: boolean
  }
  handlers: {
    onClickRePayment: () => void
    onClickPayment: () => void
    onClosePaymentModal: () => void
    onClickShowTicketBtn: () => void
    radioHandler: (event: React.ChangeEvent<HTMLInputElement>) => void
    onClickHeaderBackBtn: () => void
  }
}

const useSharedParkinglotPurchaseViewModel = (
  data: SharedParkinglotDetail,
  carNum: string,
  enteringTime: number,
  price: number,
  token: string,
  authCode: number,
  setIsConfirmOpen: Dispatch<SetStateAction<boolean>>
): SharedParkinglotPurchaseViewModel => {
  const router = useRouter()
  const headerRef = useRef<HTMLDivElement | null>(null)
  const footerRef = useRef<HTMLDivElement | null>(null)

  const { purchaseStep: step, setPurchaseStep: setStep } = Shared.States.usePurchaseStep()
  const { isPurchase, setIsPurchase } = Shared.States.useIsPurchase()
  const [paymentMethodList, setList] = useState<PaymentMethodList>(initList)
  const [frameUrl, setFrameUrl] = useState('')
  const [guestCode, setGuestCode] = useState('')
  const [toastText, setToastText] = useState('')
  const [windowHeight, setWindowHeight] = useState(0)
  const [ticketSeq, setTicketSeq] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)
  const [isErrorToast, setIsErrorToast] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isInComplete, setIsInComplete] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const radioHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value
    const updatedRadioList = paymentMethodList.map((radio) =>
      radio.value === selectedValue ? { ...radio, checked: true } : { ...radio, checked: false }
    )
    setList(updatedRadioList)
  }
  const receiveMessage = useCallback((e: MessageEvent<PostMessage>) => {
    if (e.origin !== process.env.NEXT_PUBLIC_MODU_API_HOST) return
    if (e.data.result === 'success' && e.data.parkingSeq && e.data.guestCode) {
      setFrameUrl('')
      setIsComplete(true)
      setIsOpen(false)
      paymentEvents.shared.completePayment(e.data.parkingSeq, SHARE_EXTEND.NON_EXTEND)
      setTicketSeq(e.data.parkingSeq)
      setGuestCode(e.data.guestCode)
    }
    if (e.data.result === 'failure') {
      setFrameUrl('')
      setIsInComplete(true)
    }
  }, [])
  const onClickPayment = async () => {
    paymentEvents.shared.choosePaymentMethod()
    const payment = paymentMethodList.find((el) => el.checked === true)
    if (payment && enteringTime && payment.id && token && authCode) {
      const pathVar = {
        pgType: payment.id
      }
      const payload = {
        carNum: carNum,
        shareSeq: data.seq,
        usingTime: enteringTime,
        price: price,
        point: 0,
        totalPrice: price,
        platform: PLATFORM_TYPE.WEB,
        authCodeSeq: authCode
      }
      const auth = {
        token: token
      }
      try {
        if (!isMobile) setIsOpen((prev) => !prev)
        const { data } = await requestSharedParkinglotPayment(pathVar, payload, auth)
        const url = data.redirectUrl
        if (payment.id === 'naverpay') {
          router.replace(url)
        } else {
          setFrameUrl(url)
        }
        window.addEventListener('message', receiveMessage, false)
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setFrameUrl('')
          setIsErrorToast(true)
          setToastText(err.message)
        } else {
          throw err
        }
      }
    }
  }
  const onClickRePayment = () => {
    setIsOpen(false)
    setIsInComplete(false)
    setFrameUrl('')
    setList([...initList])
  }
  const onClosePaymentModal = () => {
    setIsOpen((prev) => !prev)
  }
  const onClickShowTicketBtn = () => {
    setIsPurchase(false)
    setStep(0)
    router.replace(
      `/my-ticket/${ticketSeq}?type=${PARKINGLOT_TYPE.SHARE.toLowerCase()}&guestCode=${guestCode}&token=${token}`
    )
  }
  const onClickHeaderBackBtn = () => {
    setStep(3)
    setIsConfirmOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('message', receiveMessage)
    }
  }, [])

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight ?? 0
    const footerHeight = footerRef.current?.offsetHeight ?? 0
    setContentHeight(windowHeight - headerHeight - footerHeight)
  }, [headerRef, footerRef, windowHeight])

  return {
    texts: {},
    states: {
      step,
      toastText,
      isOpen,
      frameUrl,
      isComplete,
      isInComplete,
      contentHeight,
      windowHeight,
      headerRef,
      footerRef,
      paymentMethodList,
      ticketSeq,
      isErrorToast,
      isPurchase
    },
    handlers: {
      onClosePaymentModal,
      onClickPayment,
      onClickRePayment,
      radioHandler,
      onClickShowTicketBtn,
      onClickHeaderBackBtn
    }
  }
}

export default useSharedParkinglotPurchaseViewModel
